// Silent session renewal for the sliding 7-day isi_token.
//
// The token slides: every GET /user re-arms its full 7-day lifetime on the
// API side, so an active session never expires. Two mechanisms keep it fresh:
//
// 1. Keep-alive (startSessionKeepAlive): an hourly GET /user so a tab left
//    open keeps sliding the window without any navigation. If the session is
//    definitively dead (the API answers with a login_url), navigate to the
//    login UI — same as App.vue's initial auth check. Transient network/5xx
//    failures are ignored; the next tick or the 403 interceptor covers them.
//
// 2. 403 heal (installAuthRefresh): backstop for a token that expired anyway
//    (e.g. laptop asleep past 7 days). On a 403 from a cookie-authed request
//    to our API it re-runs GET /user (which exchanges the long-lived Kratos
//    session cookie for a fresh isi_token), then retries the failed request
//    once. A genuine permission 403 still fails (the retry 403s again and
//    propagates), and a dead Kratos session redirects to the login UI via
//    the login_url the API returns.

import { appendLangParam } from "../i18n";
import axios from "axios";
import { ISI_API_URL, ISI_API_BACKUP_URL } from "../config";

const USER_ENDPOINT = ISI_API_URL + "user";
// While failed over (utils/apiFailover.js) requests carry the backup base —
// session renewal must recognize those as "our API" too.
const BACKUP_USER_ENDPOINT = ISI_API_BACKUP_URL
  ? ISI_API_BACKUP_URL + "user"
  : "";

// Single-flight: when the token expires, every in-flight poll on the page
// 403s at once — they must share one re-auth call, not stampede /user.
let refreshPromise = null;

// Registered by startSessionKeepAlive so successful refreshes also update
// Vuex — grant/admin changes picked up by the re-mint reach the route
// guards and UI, not just the cookie.
let vuexStore = null;

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .get(USER_ENDPOINT, { withCredentials: true, _skipAuthRetry: true })
      .then((response) => {
        if (vuexStore) {
          vuexStore.commit("update_user_data", response.data);
        }
        return response;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function shouldAttemptRefresh(error) {
  const cfg = error.config;
  if (!cfg || cfg._skipAuthRetry || cfg._authRetried) return false;
  if (!error.response || error.response.status !== 403) return false;
  // Only cookie-authed requests to our own API — the expired-isi_token case.
  if (!cfg.withCredentials) return false;
  const url = cfg.url || "";
  const onPrimary = url.startsWith(ISI_API_URL);
  const onBackup = !!ISI_API_BACKUP_URL && url.startsWith(ISI_API_BACKUP_URL);
  if (!onPrimary && !onBackup) return false;
  // The auth check itself (App.vue) already handles its own failures.
  if (
    url === USER_ENDPOINT ||
    (BACKUP_USER_ENDPOINT && url === BACKUP_USER_ENDPOINT)
  ) {
    return false;
  }
  return true;
}

const KEEP_ALIVE_INTERVAL_MS = 60 * 60 * 1000;

export function startSessionKeepAlive(store) {
  vuexStore = store;
  setInterval(async () => {
    try {
      await refreshSession();
    } catch (err) {
      // A login_url in the response is the API's definitive "no valid
      // session" answer — go log in again. Anything else (network blip,
      // 5xx) is left alone.
      const data = err.response && err.response.data;
      if (data && data.login_url) {
        window.location.href = appendLangParam(data.login_url);
      }
    }
  }, KEEP_ALIVE_INTERVAL_MS);
}

export function installAuthRefresh() {
  axios.interceptors.response.use(undefined, async (error) => {
    if (!shouldAttemptRefresh(error)) {
      return Promise.reject(error);
    }
    try {
      await refreshSession();
    } catch (refreshErr) {
      // Re-auth failed. If the Kratos session itself is gone the API sends a
      // login_url — a real re-login is needed, so navigate to it.
      const data = refreshErr.response && refreshErr.response.data;
      if (data && data.login_url) {
        window.location.href = appendLangParam(data.login_url);
      }
      return Promise.reject(error);
    }
    const cfg = error.config;
    cfg._authRetried = true;
    return axios(cfg);
  });
}
